'use client';

import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';

import { useCallbackRef } from '../ref/use-callback-ref';
import { isSetStateFunction } from './is-set-state-function';

type UseControllableStateParams<T> = {
  value?: T | undefined;
  defaultValue?: T | undefined;
  onChange?: (state: T) => void;
};

export function useControllableState<T>({
  value,
  defaultValue,
  onChange,
}: UseControllableStateParams<T>): [state: T | undefined, setState: Dispatch<SetStateAction<T | undefined>>] {
  const [uncontrolledValue, setUncontrolledValue] = useUncontrolledState({ defaultValue, onChange });
  // Known Issue: when pass the prop to undefined and set value manually, uncontrolled state will be break.
  const isControlled = value !== undefined;
  const innerValue = isControlled ? value : uncontrolledValue;
  const handleChange = useCallbackRef(onChange);

  const setValue: Dispatch<SetStateAction<T | undefined>> = useCallback(
    (nextValue) => {
      if (isControlled) {
        const normalizedNextValue = isSetStateFunction(nextValue) ? nextValue(value) : nextValue;
        if (normalizedNextValue !== value) {
          handleChange.current?.(normalizedNextValue as T);
        }
      } else {
        setUncontrolledValue(nextValue);
      }
    },
    [isControlled, value, handleChange, setUncontrolledValue],
  );

  return [innerValue, setValue];
}

function useUncontrolledState<T>({
  defaultValue,
  onChange,
}: Omit<UseControllableStateParams<T>, 'prop'>): [
  state: T | undefined,
  setState: Dispatch<SetStateAction<T | undefined>>,
] {
  const state = useState<T | undefined>(defaultValue);
  const [value] = state;
  const prevValueRef = useRef(value);
  const handleChange = useCallbackRef(onChange);

  if (prevValueRef.current !== value) {
    prevValueRef.current = value;
    handleChange.current?.(value as T);
  }

  return state;
}
